import { ApiClient } from './client';
import { AUTH_API_BASE_URL } from '../utils/constants';
import type { LoginCredentials, AuthSession, IAuthApi } from '../types/auth';

const AUTH_STORAGE_KEY = 'auth_session';

/** Ответ POST /api/v1/token */
interface TokenResponse {
  access_token: string;
  token_type: string;
}

function mapTokenResponseToSession(data: TokenResponse, login: string): AuthSession {
  return {
    accessToken: data.access_token,
    tokenType: data.token_type,
    user: {
      id: `user_${Date.now()}`,
      login,
      displayName: login,
    },
    expiresAt: undefined,
  };
}

/**
 * Реальный API авторизации: POST {AUTH_API_BASE_URL}/api/v1/token с телом { username, password }.
 * В dev через прокси на /auth-api/api/v1
 */
class RealAuthApi implements IAuthApi {
  private client: ApiClient;
  constructor(client: ApiClient) {
    this.client = client;
  }

  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const username = credentials.login.trim();
    if (!username || !credentials.password) {
      throw new Error('Введите логин и пароль');
    }

    const data = await this.client.post<TokenResponse>('token', {
      username,
      password: credentials.password,
    });

    const session = mapTokenResponseToSession(data, username);
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
    } catch {
      throw new Error('Не удалось сохранить сессию');
    }
    return session;
  }

  async logout(): Promise<void> {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  async getCurrentSession(): Promise<AuthSession | null> {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return null;
      const session = JSON.parse(raw) as AuthSession;
      if (session.expiresAt && session.expiresAt < Date.now()) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }
      return session;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }
}

/** Экземпляр API авторизации (POST на AUTH_API_BASE_URL/api/v1/token). */
export const authApi: IAuthApi = new RealAuthApi(new ApiClient({ baseURL: `${AUTH_API_BASE_URL}/api/v1` }));
